import { useEffect, useMemo, useState } from 'react';
import { useRouter } from 'next/router';
import Button from '@material-ui/core/Button';
import CircularProgress from '@material-ui/core/CircularProgress';
import Divider from '@material-ui/core/Divider';
import Fab from '@material-ui/core/Fab';
import IconButton from '@material-ui/core/IconButton';
import Paper from '@material-ui/core/Paper';
import TextField from '@material-ui/core/TextField';
import Tooltip from '@material-ui/core/Tooltip';
import Typography from '@material-ui/core/Typography';
import { makeStyles } from '@material-ui/core/styles';
import BuildIcon from '@material-ui/icons/Build';
import ChatIcon from '@material-ui/icons/Chat';
import CloseIcon from '@material-ui/icons/Close';
import SendIcon from '@material-ui/icons/Send';
import VisibilityOffIcon from '@material-ui/icons/VisibilityOff';
import Markdown from 'markdown-to-jsx';
import { useQueryClient } from 'react-query';
import { toast } from 'react-toastify';
import useAuthedUser from '../hooks/useAuthedUser';
import { useAuth } from './AuthProvider';
import { publicRoutes } from '../constants';
import ApiError from '../utils/ApiError';
import {
  ASSISTANT_CHATBOT_VISIBILITY_EVENT,
  getAssistantChatbotHidden,
  setAssistantChatbotHidden,
} from '../utils/assistantChatbotPreferences';
import {
  ASSISTANT_TEMPLATE_SELECTION_EVENT,
  getAssistantTemplateSelection,
} from '../utils/assistantTemplateSelection';
import { getAssistantPageTitle } from '../utils/assistantPages';
import { hasRole, roles } from '../lib/permissions';

const useStyles = makeStyles((theme) => ({
  fab: {
    position: 'fixed',
    right: theme.spacing(3),
    bottom: theme.spacing(3),
    zIndex: theme.zIndex.drawer + 200,
  },
  panel: {
    position: 'fixed',
    right: theme.spacing(3),
    bottom: theme.spacing(3),
    zIndex: theme.zIndex.drawer + 300,
    width: 380,
    maxWidth: 'calc(100vw - 32px)',
    height: 560,
    maxHeight: 'calc(100vh - 32px)',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: theme.spacing(1.5, 2),
  },
  headerActions: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.5),
  },
  hideButton: {
    minWidth: 0,
    padding: theme.spacing(0.5, 1),
  },
  messages: {
    flex: 1,
    overflowY: 'auto',
    padding: theme.spacing(2),
    backgroundColor: '#f7f8fa',
  },
  message: {
    padding: theme.spacing(1.25, 1.5),
    borderRadius: 8,
    marginBottom: theme.spacing(1.25),
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word',
  },
  userMessage: {
    marginLeft: theme.spacing(4),
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
  },
  assistantMessage: {
    marginRight: theme.spacing(4),
    backgroundColor: '#fff',
    color: theme.palette.text.primary,
    border: `1px solid ${theme.palette.divider}`,
  },
  quickActions: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(1),
    padding: theme.spacing(1.5, 2, 0),
    backgroundColor: '#f7f8fa',
  },
  modeToggle: {
    display: 'flex',
    gap: theme.spacing(1),
    padding: theme.spacing(1.25, 2),
    backgroundColor: '#f7f8fa',
  },
  proposal: {
    marginTop: theme.spacing(1),
    padding: theme.spacing(1),
    borderRadius: 8,
    border: `1px solid ${theme.palette.divider}`,
    backgroundColor: '#fafafa',
  },
  proposalActions: {
    display: 'flex',
    justifyContent: 'flex-end',
    marginTop: theme.spacing(1),
  },
  composer: {
    zIndex: theme.zIndex.drawer + 400,
    display: 'flex',
    gap: theme.spacing(1),
    padding: theme.spacing(1.5),
    alignItems: 'flex-end',
  },
  sendButton: {
    minWidth: 44,
    height: 44,
  },
}));

async function sendAssistantMessage({ input, pathname, messages }) {
  const response = await fetch('/api/assistant/chat', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ input, pathname, messages }),
  });
  const responseBody = await response.json();
  if (!response.ok) throw new ApiError(responseBody.message, response.status);
  return responseBody.message;
}

async function sendTileEditMessage({
  input,
  pathname,
  templateId,
  messages,
  sessionTileIds,
  activeSessionTileId,
}) {
  const response = await fetch('/api/assistant/tile-edit', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      input,
      pathname,
      templateId,
      messages,
      sessionTileIds,
      activeSessionTileId,
    }),
  });
  const responseBody = await response.json();
  if (!response.ok) throw new ApiError(responseBody.message, response.status);
  return responseBody;
}

async function applyTileEditProposal({
  pathname,
  templateId,
  proposal,
  sessionTileIds,
}) {
  const response = await fetch('/api/assistant/tile-edit', {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      pathname,
      templateId,
      proposal,
      sessionTileIds,
    }),
  });
  const responseBody = await response.json();
  if (!response.ok) throw new ApiError(responseBody.message, response.status);
  return responseBody;
}

function ChatMessageContent({ content }) {
  return (
    <Markdown
      options={{
        forceWrapper: true,
        wrapper: (props) => (
          <Typography variant="body2" component="div" {...props} />
        ),
        overrides: {
          p: {
            props: {
              style: { margin: 0 },
            },
          },
        },
      }}
    >
      {content}
    </Markdown>
  );
}

export default function AssistantChatbot() {
  const classes = useStyles();
  const router = useRouter();
  const queryClient = useQueryClient();
  const pageTitle = useMemo(
    () => getAssistantPageTitle(router.pathname),
    [router.pathname]
  );
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState('help');
  const [input, setInput] = useState('');
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [hidden, setHidden] = useState(false);
  const [applyingMessageIndex, setApplyingMessageIndex] = useState(null);
  const [templateSelection, setTemplateSelection] = useState(null);
  const [sessionTiles, setSessionTiles] = useState([]);
  const [activeSessionTileId, setActiveSessionTileId] = useState(null);
  const [preferenceLoaded, setPreferenceLoaded] = useState(false);
  const authedUser = useAuthedUser();
  const { user } = useAuth();
  const canUseTileEdit =
    authedUser?.data?.type === 'SUPERADMIN' &&
    router.pathname === '/templates' &&
    !!templateSelection?.templateId &&
    !!templateSelection?.canManageTemplates &&
    !!templateSelection?.isEditing;

  useEffect(() => {
    setMessages([]);
    setInput('');
    setMode('help');
    setSessionTiles([]);
    setActiveSessionTileId(null);
  }, [router.pathname]);

  useEffect(() => {
    setMessages([]);
    setInput('');
    setMode('help');
    setSessionTiles([]);
    setActiveSessionTileId(null);
  }, [templateSelection?.templateId]);

  useEffect(() => {
    function syncHiddenPreference(event) {
      if (event?.detail && typeof event.detail.hidden === 'boolean') {
        setHidden(event.detail.hidden);
        return;
      }

      setHidden(getAssistantChatbotHidden());
    }

    syncHiddenPreference();
    setPreferenceLoaded(true);

    window.addEventListener(
      ASSISTANT_CHATBOT_VISIBILITY_EVENT,
      syncHiddenPreference
    );
    window.addEventListener('storage', syncHiddenPreference);

    return () => {
      window.removeEventListener(
        ASSISTANT_CHATBOT_VISIBILITY_EVENT,
        syncHiddenPreference
      );
      window.removeEventListener('storage', syncHiddenPreference);
    };
  }, []);

  useEffect(() => {
    function syncTemplateSelection(event) {
      if (event?.detail && 'selection' in event.detail) {
        setTemplateSelection(event.detail.selection);
        return;
      }

      setTemplateSelection(getAssistantTemplateSelection());
    }

    syncTemplateSelection();
    window.addEventListener(
      ASSISTANT_TEMPLATE_SELECTION_EVENT,
      syncTemplateSelection
    );
    window.addEventListener('storage', syncTemplateSelection);

    return () => {
      window.removeEventListener(
        ASSISTANT_TEMPLATE_SELECTION_EVENT,
        syncTemplateSelection
      );
      window.removeEventListener('storage', syncTemplateSelection);
    };
  }, []);

  useEffect(() => {
    if (!canUseTileEdit && mode === 'edit') {
      setMode('help');
    }
  }, [canUseTileEdit, mode]);

  function highlightTile(tileId) {
    setTimeout(() => {
      const el = document.getElementById(tileId);
      el?.scrollIntoView({ block: 'center' });
      el?.classList.add('animate-pulse');
      setTimeout(() => {
        el?.classList.remove('animate-pulse');
      }, 5000);
    }, 250);
  }

  async function applyProposal(messageIndex, proposal) {
    if (!proposal || applyingMessageIndex !== null) return;

    setApplyingMessageIndex(messageIndex);
    try {
      const result = await applyTileEditProposal({
        pathname: router.pathname,
        templateId: templateSelection?.templateId,
        proposal,
        sessionTileIds: sessionTiles.map((tile) => tile.id),
      });
      const appliedTiles = result.tiles?.length
        ? result.tiles
        : [
            {
              id: result.tileId,
              title: proposal.tile?.title || 'Untitled',
            },
          ];
      const tileInvalidations = appliedTiles.flatMap((tile) => [
        queryClient.invalidateQueries(['tile', tile.id]),
        queryClient.invalidateQueries(['tileData', { id: tile.id }]),
      ]);
      await Promise.all([
        queryClient.invalidateQueries([
          'template',
          { templateId: result.templateId },
        ]),
        ...tileInvalidations,
      ]);
      setMessages((currentMessages) =>
        currentMessages.map((message, index) =>
          index === messageIndex
            ? { ...message, applied: true, applyResult: result.message }
            : message
        )
      );
      toast(result.message, { type: 'success', toastId: 'tile-edit-applied' });
      const appliedTileIds = new Set(appliedTiles.map((tile) => tile.id));
      setSessionTiles((currentTiles) => [
        ...currentTiles.filter((tile) => !appliedTileIds.has(tile.id)),
        ...appliedTiles,
      ]);
      const lastAppliedTile = appliedTiles.at(-1);
      setActiveSessionTileId(lastAppliedTile.id);
      highlightTile(lastAppliedTile.id);
    } catch (error) {
      toast(error.message || 'Failed to apply tile change.', {
        type: 'error',
      });
    } finally {
      setApplyingMessageIndex(null);
    }
  }

  async function submitMessage(messageText = input) {
    const cleanInput = messageText.trim();
    if (!cleanInput || loading) return;

    const userMessage = { role: 'user', content: cleanInput };
    const nextMessages = [...messages, userMessage];

    setMessages(nextMessages);
    setInput('');
    setLoading(true);

    try {
      if (mode === 'edit') {
        const assistantResponse = await sendTileEditMessage({
          input: cleanInput,
          pathname: router.pathname,
          templateId: templateSelection?.templateId,
          messages: nextMessages,
          sessionTileIds: sessionTiles.map((tile) => tile.id),
          activeSessionTileId,
        });
        setMessages([
          ...nextMessages,
          {
            role: 'assistant',
            content: assistantResponse.message,
            proposal: assistantResponse.proposal,
            pendingRequest: assistantResponse.pendingRequest,
          },
        ]);
      } else {
        const assistantResponse = await sendAssistantMessage({
          input: cleanInput,
          pathname: router.pathname,
          messages: nextMessages,
        });
        setMessages([
          ...nextMessages,
          { role: 'assistant', content: assistantResponse },
        ]);
      }
    } catch (error) {
      setMessages([
        ...nextMessages,
        {
          role: 'assistant',
          content:
            error.message ||
            'I could not answer that right now. Please try again.',
        },
      ]);
    } finally {
      setLoading(false);
    }
  }

  function handleKeyDown(event) {
    if (event.key !== 'Enter' || event.shiftKey) return;

    event.preventDefault();
    submitMessage();
  }

  function hideChatbot() {
    setAssistantChatbotHidden(true);
    setHidden(true);
    setOpen(false);
    toast('Chatbot hidden. You can re-enable it from Settings.', {
      type: 'info',
    });
  }

  function renderProposal(message, index) {
    if (!message.proposal) return null;

    const { proposal } = message;
    const tileProposals = proposal.proposals?.length
      ? proposal.proposals
      : [proposal];
    const isBatch = tileProposals.length > 1;
    let proposalHeading = 'Edit tile';
    if (proposal.operation === 'create') proposalHeading = 'Create tile';
    if (isBatch) proposalHeading = `Create ${tileProposals.length} tiles`;
    let applyLabel = isBatch ? 'Apply all' : 'Apply';
    if (message.applied) applyLabel = 'Applied';

    return (
      <div className={classes.proposal}>
        <Typography variant="subtitle2">{proposalHeading}</Typography>
        {tileProposals.map((tileProposal, tileIndex) => {
          const tile = tileProposal.tile || {};
          const changeLines = tileProposal.changes?.length
            ? tileProposal.changes
            : [tileProposal.summary];
          const aspectSystemLabels = (tile.aspectSystems || [])
            .map((aspectSystem) =>
              [aspectSystem.aspectName, aspectSystem.systemName]
                .filter(Boolean)
                .join(' / ')
            )
            .filter(Boolean);
          const comparisonLabels = (tile.comparisons || [])
            .map((comparison) => comparison.titleText)
            .filter(Boolean);
          const showAxes = ['SCATTER', 'TM'].includes(tile.type);
          const xAxisLabels = (tile.xColumns || [])
            .map((column) => column.titleText)
            .filter(Boolean);
          const yAxisLabels = (tile.yColumns || [])
            .map((column) => column.titleText)
            .filter(Boolean);
          const filteredYearGroups = Array.from(
            new Set(
              (tile.xColumns || [])
                .map((column) => column.yearGroupName)
                .filter(Boolean)
            )
          );
          const rangeLabels = tile.specRangeLabels?.length
            ? tile.specRangeLabels
            : (tile.specRangeOpts || [])
                .slice(0, -1)
                .map(
                  (start, rangeIndex) =>
                    `${start}-${tile.specRangeOpts[rangeIndex + 1]}`
                );

          return (
            <div
              key={`${tile.title || 'tile'}-${tileIndex}`}
              style={{
                marginTop: 8,
                paddingTop: isBatch && tileIndex ? 8 : 0,
                borderTop:
                  isBatch && tileIndex ? '1px solid #e0e0e0' : 'none',
              }}
            >
              {tileProposal.targetTileTitle && (
                <Typography variant="body2" color="textSecondary">
                  Target: {tileProposal.targetTileTitle}
                </Typography>
              )}
              <Typography variant="body2">
                Title: {tile.title || 'Untitled'}
              </Typography>
              <Typography variant="body2">
                Type: {tile.type || 'TABLE'} | Figure:{' '}
                {tile.figure || 'INDIVIDUAL'}
              </Typography>
              {!!aspectSystemLabels.length && (
                <Typography variant="body2" color="textSecondary">
                  Data: {aspectSystemLabels.join(', ')}
                </Typography>
              )}
              {!!comparisonLabels.length && (
                <Typography variant="body2" color="textSecondary">
                  Comparisons: {comparisonLabels.join(', ')}
                </Typography>
              )}
              {!!rangeLabels.length && (
                <Typography variant="body2" color="textSecondary">
                  Ranges: {rangeLabels.join(', ')}
                </Typography>
              )}
              {!!filteredYearGroups.length && (
                <Typography variant="body2" color="textSecondary">
                  Filtered columns: {filteredYearGroups.join(', ')}
                </Typography>
              )}
              {showAxes && !!xAxisLabels.length && (
                <Typography variant="body2" color="textSecondary">
                  X axis: {xAxisLabels.join(', ')}
                </Typography>
              )}
              {showAxes && !!yAxisLabels.length && (
                <Typography variant="body2" color="textSecondary">
                  Y axis: {yAxisLabels.join(', ')}
                </Typography>
              )}
              {changeLines.filter(Boolean).map((change) => (
                <Typography
                  key={change}
                  variant="body2"
                  color="textSecondary"
                >
                  {change}
                </Typography>
              ))}
            </div>
          );
        })}
        <div className={classes.proposalActions}>
          <Button
            color="primary"
            variant="contained"
            size="small"
            disabled={message.applied || applyingMessageIndex !== null}
            onClick={() => applyProposal(index, proposal)}
          >
            {applyLabel}
          </Button>
        </div>
        {message.applyResult && (
          <Typography variant="caption" color="textSecondary">
            {message.applyResult}
          </Typography>
        )}
      </div>
    );
  }

  if (!user || !hasRole(user, [roles.SUPERADMIN]) || publicRoutes.includes(router.pathname)) return null;
  if (!preferenceLoaded || hidden) return null;

  if (!open) {
    return (
      <Tooltip title="Ask for Traciwr help">
        <Fab
          color="primary"
          aria-label="Ask for Traciwr help"
          className={classes.fab}
          onClick={() => setOpen(true)}
        >
          <ChatIcon />
        </Fab>
      </Tooltip>
    );
  }

  return (
    <Paper elevation={8} className={classes.panel}>
      <div className={classes.header}>
        <div>
          <Typography variant="subtitle1">Traciwr Help</Typography>
          <Typography variant="caption" color="textSecondary">
            {pageTitle}
          </Typography>
        </div>
        <div className={classes.headerActions}>
          <Tooltip title="Hide chatbot">
            <Button
              size="small"
              className={classes.hideButton}
              startIcon={<VisibilityOffIcon fontSize="small" />}
              onClick={hideChatbot}
            >
              Hide
            </Button>
          </Tooltip>
          <IconButton
            aria-label="Close Traciwr help"
            size="small"
            onClick={() => setOpen(false)}
          >
            <CloseIcon />
          </IconButton>
        </div>
      </div>
      <Divider />
      {canUseTileEdit && (
        <>
          <div className={classes.modeToggle}>
            <Button
              size="small"
              color={mode === 'help' ? 'primary' : 'default'}
              variant={mode === 'help' ? 'contained' : 'outlined'}
              startIcon={<ChatIcon fontSize="small" />}
              onClick={() => setMode('help')}
            >
              Help
            </Button>
            <Tooltip title="Review and apply tile changes">
              <Button
                size="small"
                color={mode === 'edit' ? 'primary' : 'default'}
                variant={mode === 'edit' ? 'contained' : 'outlined'}
                startIcon={<BuildIcon fontSize="small" />}
                onClick={() => setMode('edit')}
              >
                Edit tile
              </Button>
            </Tooltip>
          </div>
          <Divider />
        </>
      )}
      <div className={classes.messages}>
        {messages.length === 0 && (
          <Typography color="textSecondary" variant="body2">
            {mode === 'edit'
              ? `Ask for a tile change in ${templateSelection?.templateName}.`
              : 'Ask how to use Traciwr, including features on other pages.'}
          </Typography>
        )}
        {messages.map((message, index) => (
          <div
            key={`${message.role}-${index}`}
            className={`${classes.message} ${
              message.role === 'user'
                ? classes.userMessage
                : classes.assistantMessage
            }`}
          >
            <ChatMessageContent content={message.content} />
            {renderProposal(message, index)}
          </div>
        ))}
        {loading && <CircularProgress size={22} />}
      </div>
      <Divider />
      <div className={classes.composer}>
        <TextField
          fullWidth
          multiline
          maxRows={4}
          variant="outlined"
          size="small"
          label={
            mode === 'edit' ? 'Ask for a tile change' : 'Ask how to use Traciwr'
          }
          value={input}
          onChange={(event) => setInput(event.target.value)}
          onKeyDown={handleKeyDown}
          disabled={loading || (mode === 'edit' && !canUseTileEdit)}
        />
        <Button
          color="primary"
          variant="contained"
          className={classes.sendButton}
          onClick={() => submitMessage()}
          disabled={
            loading || !input.trim() || (mode === 'edit' && !canUseTileEdit)
          }
          aria-label="Send message"
        >
          <SendIcon fontSize="small" />
        </Button>
      </div>
    </Paper>
  );
}
